import type { TokenStorage } from './types';

const DEFAULT_ACCESS_KEY = 'access_token';
const DEFAULT_REFRESH_KEY = 'refresh_token';

/** Formato do objeto de auth salvo no localStorage (token = access token) */
interface StoredAuthData {
  token?: string;
  refreshToken?: string;
  [key: string]: unknown;
}

/**
 * TokenStorage que lê/grava token e refreshToken no mesmo JSON de auth (ex.: e-learning-b2b-auth-data).
 * Assim o login (saveAuthData) e o refresh (setTokens) usam a mesma fonte; o client encontra o refresh e não redireciona para login no 401.
 */
export function createAuthDataTokenStorage(authDataKey: string): TokenStorage {
  return {
    getAccessToken: () => {
      try {
        const raw = localStorage.getItem(authDataKey);
        if (!raw) return null;
        const data = JSON.parse(raw) as StoredAuthData;
        return typeof data.token === 'string' ? data.token : null;
      } catch {
        return null;
      }
    },
    getRefreshToken: () => {
      try {
        const raw = localStorage.getItem(authDataKey);
        if (!raw) return null;
        const data = JSON.parse(raw) as StoredAuthData;
        return typeof data.refreshToken === 'string' ? data.refreshToken : null;
      } catch {
        return null;
      }
    },
    setTokens: (accessToken, refreshToken) => {
      try {
        const raw = localStorage.getItem(authDataKey);
        const data: StoredAuthData = raw ? (JSON.parse(raw) as StoredAuthData) : {};
        data.token = accessToken;
        data.refreshToken = refreshToken;
        localStorage.setItem(authDataKey, JSON.stringify(data));
      } catch {
        // fallback: gravar só os dois campos em objeto novo
        localStorage.setItem(
          authDataKey,
          JSON.stringify({ token: accessToken, refreshToken })
        );
      }
    },
    clearTokens: () => {
      try {
        localStorage.removeItem(authDataKey);
      } catch {
        // ignore
      }
    },
  };
}

export function createLocalStorageTokenStorage(
  accessKey = DEFAULT_ACCESS_KEY,
  refreshKey = DEFAULT_REFRESH_KEY
): TokenStorage {
  return {
    getAccessToken: () => localStorage.getItem(accessKey),
    getRefreshToken: () => localStorage.getItem(refreshKey),
    setTokens: (accessToken, refreshToken) => {
      localStorage.setItem(accessKey, accessToken);
      localStorage.setItem(refreshKey, refreshToken);
    },
    clearTokens: () => {
      localStorage.removeItem(accessKey);
      localStorage.removeItem(refreshKey);
    },
  };
}
